import { GoogleMap, InfoWindow, LoadScript, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { Col, ConfigProvider, Drawer, List, Row, Table } from 'antd'
import moment from 'moment';
import { useCallback, useMemo, useRef, useState } from 'react';
import { google_maps_key } from '../../Components/config';
import Car from '../../assets/icons/drive.gif';
import Break from '../../assets/icons/stop.gif';

const containerStyle = {
    width: "100%",
    height: "calc(100vh - 100px)",
};

const ActivityLog = ({ open, onClose, activityLog }) => {
    const [activeMarker, setActiveMarker] = useState(null);

    const columns = [
        {
            title: 'Action',
            dataIndex: 'operation',
            width: 110,
            render: (type) => {
                const isIn = type === 1;
                return (
                    <div className={`check-status ${isIn ? 'in' : 'out'}`}>
                        <span className="status-dot" />
                        <span>{isIn ? 'Driving' : 'Break'}</span>
                    </div>
                );
            },
        },
        {
            title: 'Date & Time',
            dataIndex: 'createdAt',
            width: 150,
            render: (date) => date && moment(date).format('DD-MM-YYYY HH:mm:ss'),
        }
    ];

    return (
        <Drawer
            title="Manual Tacho Activity Log"
            placement="bottom"
            height={'100vh'}
            onClose={onClose}
            open={open}
        >
            <Row>
                <Col md={12}>
                    <ConfigProvider
                        theme={{
                            components: {
                                Table: {
                                    rowHoverBg: '#bae7ff',
                                },
                            }
                        }}
                    >
                        <Table
                            dataSource={activityLog}
                            columns={columns}
                            pagination={false}
                            onRow={(record) => ({
                                onMouseEnter: () => setActiveMarker(record.id),
                                onMouseLeave: () => setActiveMarker(null),
                            })}
                            rowClassName={(record) =>
                                activeMarker === record.id ? 'active-row' : ''
                            }
                            scroll={{ y: 'calc(100vh - 146px)' }}

                        />
                    </ConfigProvider>

                </Col>
                <Col md={12}>
                    <RouteMap data={activityLog} activeMarker={activeMarker} setActiveMarker={setActiveMarker} />
                </Col>
            </Row>

        </Drawer>
    )
}

export default ActivityLog

const RouteMap = ({ data = [], activeMarker, setActiveMarker }) => {
    const mapRef = useRef(null);
    const { isLoaded } = useJsApiLoader({ googleMapsApiKey: google_maps_key });

    const parsedPoints = useMemo(
        () => data.map((p) => ({ ...p, lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })),
        [data]
    );

    const center = useMemo(() => {
        if (!parsedPoints.length) return { lat: 0, lng: 0 };
        return { lat: parsedPoints[0].lat, lng: parsedPoints[0].lng };
    }, [parsedPoints]);

    const polylinePath = useMemo(() => parsedPoints.map((p) => ({ lat: p.lat, lng: p.lng })), [parsedPoints]);

    const onLoad = useCallback(
        (map) => {
            mapRef.current = map;
            if (!parsedPoints.length) return;
            const bounds = new window.google.maps.LatLngBounds();
            parsedPoints.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
            map.fitBounds(bounds);
        },
        [parsedPoints]
    );

    if (!isLoaded) return null;

    return (
        <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={16} onLoad={onLoad}>
            <Polyline path={polylinePath} options={{ strokeColor: '#1677ff', strokeOpacity: 1, strokeWeight: 4 }} />

            {parsedPoints.map((point) => (
                <Marker
                    key={point.id}
                    position={{ lat: point.lat, lng: point.lng }}
                    icon={{ url: point.operation === 1 ? Car : Break, scaledSize: new window.google.maps.Size(28, 28) }}
                    onClick={() => setActiveMarker(point.id)}
                    title={point.operation === 1 ? 'Driving' : 'Break'}
                >
                    {activeMarker === point.id && (
                        <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                            <div style={{ minWidth: 220, padding: 8, borderRadius: 8, fontFamily: 'Arial, sans-serif' }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                                    <img src={point.operation === 1 ? Car : Break} alt="icon" style={{ width: 20, height: 20, marginRight: 6 }} />
                                    <span style={{ fontWeight: 600, color: point.operation === 1 ? '#1677ff' : '#ff4d4f', fontSize: 14 }}>
                                        {point.operation === 1 ? 'Driving' : 'Break'}
                                    </span>
                                </div>
                                <List
                                    size="small"
                                    dataSource={[
                                        { label: 'Time', value: moment(point.createdAt).format('DD-MM-YYYY HH:mm:ss') },
                                        { label: 'Latitude', value: point.lat.toFixed(6) },
                                        { label: 'Longitude', value: point.lng.toFixed(6) },
                                    ]}
                                    renderItem={(item) => (
                                        <List.Item style={{ padding: '2px 0', fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ fontWeight: 500 }}>{item.label}</span>
                                            <span style={{ color: '#111' }}>{item.value}</span>
                                        </List.Item>
                                    )}
                                    style={{ margin: 0 }}
                                />
                            </div>
                        </InfoWindow>
                    )}
                </Marker>
            ))}
        </GoogleMap>
    );
};